import * as Sentry from '@sentry/react';
import type { ErrorEvent } from '@sentry/react';

const REDACTED = '***';
const MAX_DEPTH = 8;

// Keep in sync with node-api/src/common/utils/input-sanitise.util.ts (sanitiseBody).
// Only these exact keys are redacted; everything else is sent to Sentry as-is.
const SENSITIVE_FIELDS = [
  'password',
  'oldPassword',
  'newPassword',
  'confirmPassword',
  'Password',
  'hash',
  'userHash',
  'token',
  'apiKey',
  'CVV',
  'cvv',
  'cardNumber',
  'checksum',
  'accountNumber',
  'institutionNumber',
  'transitNumber',
];

// Errors and axios errors have non-enumerable / nested fields that would otherwise
// serialize to `{}` in Sentry, losing the actual failure reason.
function serializeError(err: any): Record<string, unknown> {
  const out: Record<string, unknown> = {
    name: err.name,
    message: err.message,
  };
  if (err.code !== undefined) out.errCode = err.code;
  if (err.response) {
    out.status = err.response.status;
    out.statusText = err.response.statusText;
    out.responseData = err.response.data;
  }
  if (err.config) {
    out.requestMethod = err.config.method;
    out.requestUrl = err.config.url;
    out.requestData = parseJson(err.config.data);
  }
  if (err.cause !== undefined) out.cause = err.cause;
  for (const key of Object.keys(err)) {
    if (!(key in out) && !['response', 'config', 'request', 'stack', 'code'].includes(key)) {
      out[key] = err[key];
    }
  }
  return out;
}

// Axios keeps the request body as a JSON string; parse it so its keys can be sanitised.
function parseJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

export function sanitizeObject(obj: any, depth = 0, seen = new WeakSet<object>()): any {
  if (!obj || typeof obj !== 'object') return obj;
  if (depth > MAX_DEPTH) return '[Truncated]';
  if (seen.has(obj)) return '[Circular]';
  seen.add(obj);

  const source = obj instanceof Error ? serializeError(obj) : obj;

  if (Array.isArray(source)) {
    return source.map((item) => sanitizeObject(item, depth + 1, seen));
  }

  const clone: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    clone[key] = SENSITIVE_FIELDS.includes(key)
      ? REDACTED
      : sanitizeObject(source[key], depth + 1, seen);
  }
  return clone;
}

// Sentry `beforeSend`: we own scrubbing (same list as the backend) instead of relying
// on Sentry's default denylist, which filtered useful fields like `error` / `errorDescription`.
export function scrubSentryEvent(event: ErrorEvent): ErrorEvent {
  if (event.extra) event.extra = sanitizeObject(event.extra);
  if (event.contexts) event.contexts = sanitizeObject(event.contexts);
  if (event.request?.data) event.request.data = sanitizeObject(event.request.data);
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => ({
      ...b,
      data: b.data ? sanitizeObject(b.data) : b.data,
    }));
  }
  return event;
}

export function logThirdPartyError(err: Error, metadata: any = {}) {
  const sanitizedMeta = sanitizeObject(metadata);

  // Always keep a local trace too, so failures are visible outside production.
  console.error(`[third_party] ${err.message}`, sanitizedMeta);

  Sentry.captureException(err, {
    level: 'error',
    extra: sanitizedMeta,
    tags: {
      source: 'third_party',
      ...(metadata?.flow ? { flow: String(metadata.flow) } : {}),
      ...(metadata?.step ? { step: String(metadata.step) } : {}),
    },
  });
}

export function logNuveiRequest(method: string, request: unknown, response: unknown) {
  const sanitized = sanitizeObject({ method, request, response });
  const result =
    response && typeof response === 'object' && 'result' in response
      ? String((response as { result: unknown }).result)
      : undefined;

  console.info(`[nuvei] ${method}`, sanitized);

  Sentry.captureMessage(`Nuvei Web SDK request - ${method}`, {
    level: 'log',
    extra: sanitized,
    tags: {
      source: 'nuvei',
      nuvei_method: method,
      ...(result ? { nuvei_result: result } : {}),
    },
  });
}

// Nuvei Web SDK methods that call Nuvei's servers and report back via a callback.
const NUVEI_LOGGED_METHODS = ['createPayment', 'addCardUpo', 'getCardDetails', 'authenticate3d'];

// Wraps the SafeCharge instance so every request to Nuvei is logged to Sentry (level `log`)
// with its request and response, and SDK exceptions are logged as errors.
export function withNuveiLogging<T extends object>(safeCharge: T): T {
  return new Proxy(safeCharge, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== 'function' || !NUVEI_LOGGED_METHODS.includes(String(prop))) {
        return value;
      }
      const method = String(prop);
      return (request: unknown, callback?: (response: unknown) => void, ...rest: unknown[]) => {
        const wrappedCallback = (response: unknown) => {
          logNuveiRequest(method, request, response);
          callback?.(response);
        };
        try {
          return value.call(target, request, wrappedCallback, ...rest);
        } catch (error) {
          logThirdPartyError(new Error(`Nuvei Web SDK ${method} threw an exception`), {
            flow: 'nuvei_sdk',
            step: method,
            request,
            response: null,
            error,
          });
          throw error;
        }
      };
    },
  });
}
